import { useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { logger } from '@/lib/logger';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { BaseModalProps } from '@/contexts/ModalContext';

export interface LogWeightSheetProps extends BaseModalProps {
  // Optional to satisfy the ModalContext showModal typing; always supplied at
  // runtime (creates a MANUAL BodyMeasurement).
  onSubmit?: (weightKg: number) => Promise<void> | void;
  defaultWeight?: string;
  portalHost?: string;
}

/**
 * Quick manual weight entry. Non-health users (web, or anyone who hasn't
 * connected a health store) log a weight here, which the caller persists as a
 * `source = MANUAL` BodyMeasurement — the same entity smart-scale imports write
 * to. Kept intentionally minimal: weight only (composition comes from a scale).
 */
export const LogWeightSheet = ({
  onSubmit,
  defaultWeight,
  onClose,
  portalHost,
}: LogWeightSheetProps) => {
  const { t } = useTranslation('user');
  const [value, setValue] = useState(defaultWeight ?? '');

  const parsed = parseFloat(value);
  const isValid = !Number.isNaN(parsed) && parsed > 0;

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isValid) await onSubmit?.(parsed);
    },
    // Surface a failed write instead of silently keeping the sheet open (a
    // swallowed error here is what made a bad DB write look like a dead button).
    onError: (error) =>
      logger.error(error, '[Health] manual weight save failed'),
    onSuccess: () => onClose(),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent portalHost={portalHost}>
        <View testID="log-weight-sheet" className="gap-4">
          <DialogHeader>
            <DialogTitle>{t('bodyComposition.logWeightTitle')}</DialogTitle>
            <DialogDescription>
              {t('bodyComposition.logWeightSubtitle')}
            </DialogDescription>
          </DialogHeader>

          <View className="flex-row items-center rounded-lg bg-surface-elevated">
            <Input
              testID="log-weight-input"
              value={value}
              onChangeText={setValue}
              keyboardType="decimal-pad"
              placeholder="0"
              autoFocus
              className="flex-1 bg-transparent"
            />
            <Text className="pr-3 text-sm text-text-secondary">
              {t('settings.bodyMetrics.weightUnit')}
            </Text>
          </View>

          <DialogFooter>
            <Button
              testID="log-weight-save"
              variant="primary"
              onPress={() => saveMutation.mutate()}
              disabled={!isValid || saveMutation.isPending}
            >
              <Text className="font-semibold text-accent-text">
                {t('bodyComposition.logWeightSave')}
              </Text>
            </Button>
          </DialogFooter>
        </View>
      </DialogContent>
    </Dialog>
  );
};
